import { db, storage } from "../../../boot/firebase";
import { collection, deleteDoc, doc, setDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref as storageRef,
  uploadBytesResumable,
} from "firebase/storage";
import ProjectFile from "../../../classes/ProjectFile";
import FileRef from "../../../classes/FileRef";
import { pickDocumentInfo } from "../../../classes/DocumentRef";
import { normalizeUploadImageFile } from "../../../util/imageUpload";

export const LISTEN_PROJECT_FILES = (
  { dispatch },
  { projectID, next, error, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: ProjectFile,
    path: ProjectFile.getPath(projectID),
    stateKey: "projectFiles",
    next,
    error,
    unlisten,
  });
};

// Uploads one file and creates its record. The record is written only once
// the bytes are up, so the Files tab never lists a file nobody can open.
export const UPLOAD_PROJECT_FILE = async (
  { state },
  { projectID, saleID, type, info, file, onProgress }
) => {
  if (!file) throw "No file";
  if (!projectID) throw "Missing project";

  const normalized = await normalizeUploadImageFile(file);
  const docRef = doc(collection(db, ProjectFile.getPath(projectID)));
  const storagePath = ProjectFile.getStoragePath(
    projectID,
    docRef.id,
    normalized.name || file.name
  );
  const objectRef = storageRef(storage, storagePath);

  await new Promise((resolve, reject) => {
    const task = uploadBytesResumable(objectRef, normalized, {
      contentType: normalized.type || file.type || "application/octet-stream",
      customMetadata: { uploadedBy: state.user.id },
    });
    task.on(
      "state_changed",
      (snapshot) => {
        if (typeof onProgress === "function" && snapshot.totalBytes) {
          onProgress(snapshot.bytesTransferred / snapshot.totalBytes);
        }
      },
      reject,
      resolve
    );
  });

  const fileData = new FileRef({ toSave: normalized }).data;
  fileData.toSave = null;
  fileData.url = await getDownloadURL(objectRef);

  const now = new Date();
  const projectFile = new ProjectFile(projectID, docRef.id, {
    saleID: saleID || null,
    type,
    info: pickDocumentInfo(type, info),
    file: fileData,
    storagePath,
    created: now,
    createdBy: state.user.id,
    modified: now,
    modifiedBy: state.user.id,
  });
  try {
    await setDoc(docRef, projectFile.data);
  } catch (error) {
    // Don't strand the bytes when the record is refused.
    deleteObject(objectRef).catch(() => {});
    throw error;
  }
  return projectFile;
};

// Deletes the record first: if the storage delete then fails, the orphaned
// bytes are harmless, whereas the other order would leave a row pointing at
// nothing.
export const DELETE_PROJECT_FILE = async (context, projectFile) => {
  await deleteDoc(doc(db, projectFile.path));
  if (projectFile.data.storagePath) {
    try {
      await deleteObject(storageRef(storage, projectFile.data.storagePath));
    } catch (error) {
      console.error(error);
    }
  }
};
